import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";

import { hooks as colocatedHooks } from "phoenix-colocated/wsr";
import { hooks } from "../hooks";
import { startEarlyReportNumbers } from "../hooks/report-number";
import { onDocumentPatch } from "./report-transitions";

const csrfToken = document?.querySelector("meta[name='csrf-token']")?.getAttribute("content");

// oxlint-disable-next-line no-unsafe-call, no-unsafe-argument
const liveSocket = new LiveSocket("/live", Socket, {
  hooks: { ...colocatedHooks, ...hooks },
  // Long-poll transport is far more expensive than WebSockets (new request per
  // poll cycle), and at scale it drives web server CPU usage dramatically high.
  // Keep the fallback threshold high so only genuinely WS-blocked networks fall
  // back; clients with slow handshakes still connect via WS. This matters
  // disproportionately for headless-browser scrapers: their WS handshake is
  // often slow enough to trip an aggressive fallback, and each session then
  // pins a long-poll process for the lifetime of the page -- a large pool of
  // scrapers can pin every core on the web hosts this way.
  longPollFallbackMs: 10_000,
  params: { _csrf_token: csrfToken },
  dom: { onDocumentPatch },
});

startEarlyReportNumbers();

// connect if there are any LiveViews on the page
// oxlint-disable-next-line no-unsafe-member-access, no-unsafe-call
liveSocket.connect();

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()

(window as unknown as Record<string, unknown>).liveSocket = liveSocket;

// The lines below enable quality of life phoenix_live_reload
// development features:
//
//     1. stream server logs to the browser console
//     2. click on elements to jump to their definitions in your code editor
//
// oxlint-disable-next-line no-unsafe-member-access
if (process.env.NODE_ENV === "development") {
  // @ts-expect-error - provided by phoenix
  window.addEventListener("phx:live_reload:attached", ({ detail }) => {
    const reloader = detail as {
      enableServerLogs: () => void;
      openEditorAtCaller: (target: EventTarget | null) => void;
      openEditorAtDef: (target: EventTarget | null) => void;
    };

    // Enable server log streaming to client.
    // Disable with reloader.disableServerLogs()
    reloader.enableServerLogs();

    // Open configured PLUG_EDITOR at file:line of the clicked element's HEEx component
    //
    //   * click with "c" key pressed to open at caller location
    //   * click with "d" key pressed to open at function component definition location

    // oxlint-disable-next-line init-declarations
    let keyDown: string | undefined;

    window.addEventListener("keydown", (event) => (keyDown = event.key));
    window.addEventListener("keyup", (_event) => (keyDown = undefined));
    window.addEventListener(
      "click",
      (event) => {
        if (keyDown === "c") {
          event.preventDefault();
          event.stopImmediatePropagation();
          reloader.openEditorAtCaller(event.target);
        } else if (keyDown === "d") {
          event.preventDefault();
          event.stopImmediatePropagation();
          reloader.openEditorAtDef(event.target);
        }
      },
      true,
    );

    (window as unknown as Record<string, unknown>).liveReloader = reloader;
  });
}
